import type { Filter, FilterOperator, ResolvedFilter } from './filter';
import type { FieldType } from './field';
import type { SortState } from './sort';

export type Get<T> = (row: T, key: string) => unknown;

function matchesNumericOp(
    operator: FilterOperator,
    cell: number,
    lo: number,
    hi = NaN,
): boolean {
    switch (operator) {
        case 'equals':
            return cell === lo;
        case 'greaterThan':
            return cell > lo;
        case 'greaterThanOrEqual':
            return cell >= lo;
        case 'lessThan':
            return cell < lo;
        case 'lessThanOrEqual':
            return cell <= lo;
        case 'between':
            return cell >= lo && (Number.isNaN(hi) || cell <= hi);
        default:
            return false;
    }
}

function dayStart(value: unknown): number {
    const d = new Date(value as string | number | Date);
    return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
}

function matchesText(value: unknown, filter: Filter): boolean {
    const cell = String(value).toLowerCase();
    const target = String(filter.value).toLowerCase();
    return filter.operator === 'equals'
        ? cell === target
        : cell.includes(target);
}

function matchesSet(value: unknown, filter: Filter): boolean {
    const values = filter.value as string[];
    return values.length > 0 && values.includes(String(value));
}

export function matchesFilter(value: unknown, filter: Filter): boolean {
    if (value === null || value === undefined) {
        return filter.type === 'set' && (filter.value as string[]).includes('');
    }
    switch (filter.type) {
        case 'text':
            return matchesText(value, filter);
        case 'number':
            return matchesNumericOp(
                filter.operator,
                Number(value),
                Number(filter.value),
                Number(filter.valueTo),
            );
        case 'date':
            return matchesNumericOp(
                filter.operator,
                dayStart(value),
                dayStart(filter.value),
                filter.valueTo === undefined ? NaN : dayStart(filter.valueTo),
            );
        case 'set':
            return matchesSet(value, filter);
        default:
            return false;
    }
}

export function applyFilters<T>(
    rows: T[],
    filters: ResolvedFilter[],
    get: Get<T>,
): T[] {
    if (filters.length === 0) return rows;
    return rows.filter((row) =>
        filters.every((f) => matchesFilter(get(row, f.key), f)),
    );
}

export function applySearch<T>(
    rows: T[],
    text: string,
    keys: string[],
    get: Get<T>,
): T[] {
    if (!text) return rows;
    const needle = text.toLowerCase();
    return rows.filter((row) =>
        keys.some((key) =>
            String(get(row, key) ?? '')
                .toLowerCase()
                .includes(needle),
        ),
    );
}

export function applySort<T>(
    rows: T[],
    sort: SortState | null,
    get: Get<T>,
    comparator: FieldType['sort'],
): T[] {
    if (!sort) return rows;
    const dir = sort.dir === 'asc' ? 1 : -1;
    return rows
        .map((row, index) => ({ row, index }))
        .sort((a, b) => {
            const av = get(a.row, sort.key);
            const bv = get(b.row, sort.key);
            const aNil = av === null || av === undefined;
            const bNil = bv === null || bv === undefined;
            if (aNil && bNil) return a.index - b.index;
            if (aNil) return 1;
            if (bNil) return -1;
            const result = comparator(av, bv) * dir;
            return result !== 0 ? result : a.index - b.index;
        })
        .map(({ row }) => row);
}
